/**
 * @fileoverview WEB-711 — `MatchupModule`, the sports spine's two-sided
 * matchup card.
 *
 * ## What this renders
 *
 * A `--evt-matchup` CSS grid (`1fr auto 1fr` at ≥768, a single column below)
 * per `docs/design-system/event-v2-m2-module-specs.md` §2.1–§2.4: a home
 * side block (name, meta, form pips), a "VS" divider in category ink, and an
 * away side block that right-aligns above the mobile breakpoint. Form pips
 * are 18×18 squares — win green / draw neutral / loss red, per theme.
 *
 * ## Why this ships fully degraded today
 *
 * `EventDetailV2` carries **no matchup fields at all** — no home/away
 * identity, no records, no recent-form column (verified against the real
 * generated schema, not just the issue text: there is no `homeTeam`,
 * `awayTeam`, or anything sports-shaped anywhere on the wire).
 * `hasModuleContent` in `lib/eventDetail/modules.ts` already resolves
 * `matchup` to `hasContent: false` unconditionally for the same reason — that
 * is the honest state this module ships in, and this file adds no second
 * gating opinion on top of it.
 *
 * `deriveMatchupContent` is the seam a future supply project fills in; today
 * it always returns `null`, which is WHY `MatchupCard` (the grid, the VS
 * divider, the form pips) is built and tested as a separate, directly
 * testable presentational piece — `MatchupModule` itself can never produce a
 * non-null render against a real `EventDetailV2` today, so "a fully-populated
 * matchup renders both sides and five form pips each" is proven against
 * `MatchupCard` directly (see `__tests__/MatchupModule.test.tsx`).
 *
 * **Never parse `event.title`.** A title split (`"X vs. Y"`) is a guess
 * dressed as data, not a real signal — `deriveMatchupContent` never reads
 * `event.title`, and the test suite asserts this explicitly with a title
 * that contains " vs. ".
 */

import type { ReactElement } from 'react';

import { cn } from '@/components/atoms/primitives/utils';
import type { ModuleRowChroma } from '@/components/organisms/EventModuleSection';
import { EventModuleSection } from '@/components/organisms/EventModuleSection';
import type { EventDetailV2 } from '@/types/eventDetail';

import type { EventModuleProps } from './registry';

/** A single result in a side's recent-form strip, oldest-to-newest. */
export type MatchupFormResult = 'W' | 'D' | 'L';

/** One side of a matchup: its name, an optional record/context line, and recent form. */
export interface MatchupSide {
  name: string;
  /** e.g. a record — `"14-6, HOME"`. Omitted renders no meta line. */
  meta?: string | null;
  /** Oldest-to-newest. Omitted or empty renders no pip row. */
  form?: readonly MatchupFormResult[];
}

/** Both sides of a resolved matchup. */
export interface MatchupContent {
  home: MatchupSide;
  away: MatchupSide;
}

const FORM_RESULT_LABELS: Record<MatchupFormResult, string> = {
  W: 'won',
  D: 'drawn',
  L: 'lost',
};

/** Win green / draw neutral / loss red — themed via existing semantic tokens (§2.4). */
const FORM_PIP_COLOR: Record<MatchupFormResult, string> = {
  W: 'var(--success)',
  D: 'var(--fg-2)',
  L: 'var(--danger)',
};

/** "Recent form: won, won, lost, won, lost" — the group's single accessible name. */
function formAccessibleLabel(form: readonly MatchupFormResult[]): string {
  return `Recent form: ${form.map((result) => FORM_RESULT_LABELS[result]).join(', ')}`;
}

interface FormPipsProps {
  form: readonly MatchupFormResult[];
  /** Away applies `--evt-matchup-just`; home declares no justify-content at all (§2.2). */
  align: 'home' | 'away';
}

/**
 * The recent-form pip row. Individual pips are `aria-hidden` — a screen
 * reader hearing five bare letters ("W D L W L") is not meaningful — and the
 * wrapping `role="group"` carries the one real accessible name instead.
 */
function FormPips({ form, align }: FormPipsProps): ReactElement | null {
  if (form.length === 0) {
    return null;
  }

  return (
    <div
      role="group"
      aria-label={formAccessibleLabel(form)}
      data-testid="matchup-form-pips"
      // Tailwind arbitrary-property syntax — the value never passes through a
      // typed `style` object, so `justify-content`'s csstype union (which,
      // unlike `text-align`, DOES accept a bare `var(...)` via `string & {}`)
      // is a non-issue either way; kept consistent with the other two
      // matchup tokens below.
      className={cn(
        'evt-matchup__pips flex',
        align === 'away' && '[justify-content:var(--evt-matchup-just)]',
      )}
    >
      {form.map((result, index) => (
        <span
          key={`${result}-${index}`}
          aria-hidden="true"
          data-testid="matchup-form-pip"
          data-result={result}
          className={cn(
            'evt-matchup__pip grid place-items-center rounded-[3px] border border-ds-border bg-(--bg-sunken)',
            'font-mono',
          )}
          // style-dynamic: pip colour is looked up per result (W / L / D)
          style={{ color: FORM_PIP_COLOR[result] }}
        >
          {result}
        </span>
      ))}
    </div>
  );
}

interface MatchupSideBlockProps {
  side: MatchupSide;
  align: 'home' | 'away';
}

/**
 * One side's name / meta / form stack. The home side declares no alignment
 * overrides at all — it is left-aligned by default at every tier (§2.2); only
 * the away side flips via `--evt-matchup-align` / `--evt-matchup-just`.
 */
function MatchupSideBlock({ side, align }: MatchupSideBlockProps): ReactElement {
  const meta =
    typeof side.meta === 'string' && side.meta.trim().length > 0 ? side.meta.trim() : null;
  const form = side.form ?? [];
  const isAway = align === 'away';

  return (
    <div
      data-testid={`matchup-side-${align}`}
      // Tailwind arbitrary-property syntax: `text-align`'s csstype union has
      // no CSS-var escape hatch, so a typed `style` object rejects a bare
      // `var(...)` value outright. Routing it through a class instead
      // sidesteps the type system rather than casting past it — same
      // precedent as `[font-family:var(--font-mono)]` in `ModuleRowList.tsx`.
      className={cn(isAway && '[text-align:var(--evt-matchup-align)]')}
    >
      <p data-testid="matchup-side-name" className="m-0 font-bold text-fg-1 font-display text-h5">
        {side.name}
      </p>
      {meta !== null ? (
        <p
          data-testid="matchup-side-meta"
          className="evt-matchup__meta uppercase text-fg-3 font-mono text-micro tracking-caps"
        >
          {meta}
        </p>
      ) : null}
      <FormPips form={form} align={align} />
    </div>
  );
}

export interface MatchupCardProps {
  home: MatchupSide;
  away: MatchupSide;
  /** Caller-resolved category chroma. Only `catInk` is used (the VS divider). */
  chroma: ModuleRowChroma;
  /** Optional additional class names on the card. */
  className?: string;
}

/**
 * `MatchupCard` — the pure-props two-sided grid (§2.1–§2.4). Exported and
 * tested directly because `MatchupModule` can never reach a populated render
 * through a real `EventDetailV2` today — see the file header.
 */
export function MatchupCard({ home, away, chroma, className }: MatchupCardProps): ReactElement {
  return (
    <div
      data-testid="matchup-card"
      className={cn(
        'mb-5 grid items-center gap-(--evt-mgap) rounded-ds-lg border border-ds-border bg-ds-surface p-5',
        '[grid-template-columns:var(--evt-matchup)]',
        className,
      )}
    >
      <MatchupSideBlock side={home} align="home" />
      <div
        data-testid="matchup-vs"
        className="grid place-items-center uppercase font-mono text-micro tracking-eyebrow"
        // style-dynamic: VS divider colour is the caller-resolved category ink
        style={{ color: chroma.catInk }}
      >
        VS
      </div>
      <MatchupSideBlock side={away} align="away" />
    </div>
  );
}

/**
 * Awaiting supply: `EventDetailV2` has no matchup fields — no home/away
 * identity, no records, no recent-form column. Always `null` today; this is
 * the seam a future contract change fills in. Only this function's body
 * should change then.
 *
 * CRITICAL: never derive home/away by parsing `event.title` (e.g. splitting
 * on `" vs. "`). A title split is a guess dressed as data — see WEB-711.
 */
export function deriveMatchupContent(_event: EventDetailV2): MatchupContent | null {
  return null;
}

/**
 * `MatchupModule` — registered `matchup` renderer (WEB-711).
 *
 * `module.hasContent` is the outer, authoritative gate; `deriveMatchupContent`
 * is the inner one. Both currently resolve the same way (no source, no
 * render) for the same underlying reason, stated once at each seam.
 */
export function MatchupModule({ module, event, chroma }: EventModuleProps): ReactElement | null {
  if (!module.hasContent) {
    return null;
  }

  const matchup = deriveMatchupContent(event);
  if (!matchup) {
    return null;
  }

  return (
    <EventModuleSection module={module} chroma={chroma}>
      <MatchupCard home={matchup.home} away={matchup.away} chroma={chroma} />
    </EventModuleSection>
  );
}
